/**
 * ThemeQuestionFlow
 * Visar alla frågor för ett valt livstema
 * Sparar svar i sessionStorage och navigerar till signup när klart
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Loader } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'https://therapy-ai-backend-zqsx.onrender.com';

// Svarsskala (1-6)
const ANSWER_SCALE = [
  { value: 1, label: 'Stämmer inte alls' },
  { value: 2, label: 'Stämmer inte särskilt väl' },
  { value: 3, label: 'Stämmer i viss mån' },
  { value: 4, label: 'Stämmer väl' },
  { value: 5, label: 'Stämmer mycket väl' },
  { value: 6, label: 'Stämmer fullständigt' }
];

const ThemeQuestionFlow = () => {
  const navigate = useNavigate();
  const { themeId } = useParams();
  
  const [theme, setTheme] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Ladda valt tema från sessionStorage
    const savedTheme = sessionStorage.getItem('selectedLifeTheme');
    if (savedTheme) {
      setTheme(JSON.parse(savedTheme));
    }

    // Hämta frågor för temat
    fetchQuestions();

    // Ladda sparade svar om de finns
    const savedAnswers = sessionStorage.getItem('pendingLifeThemeAnswers');
    if (savedAnswers) {
      const parsed = JSON.parse(savedAnswers);
      // Filtrera till endast svar för detta tema
      const themeAnswers = parsed.filter(a => a.theme_id === parseInt(themeId));
      const answersMap = {};
      themeAnswers.forEach(a => {
        answersMap[a.question_id] = a.answer_value;
      });
      setAnswers(answersMap);
    }

    // Track page view
    trackEvent('Life Theme Question Flow Start Public', {
      flow_type: 'public_question_flow',
      theme_id: themeId,
      theme_name: savedTheme ? JSON.parse(savedTheme).name : 'Unknown'
    });
  }, [themeId]);

  const fetchQuestions = async () => {
    try {
      // Hämta frågor för detta tema (utan autentisering)
      const response = await fetch(`${API_BASE}/api/life-theme-identifier/themes/${themeId}/questions/public`);
      
      if (response.ok) {
        const data = await response.json();
        setQuestions(data);
      } else {
        throw new Error('Kunde inte hämta frågor');
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Kunde inte ladda frågor. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    // Uppdatera answers state
    const newAnswers = {
      ...answers,
      [questionId]: value
    };
    setAnswers(newAnswers);

    // Spara till sessionStorage
    saveAnswersToStorage(questionId, value);

    // Track answer
    const answeredCount = Object.keys(newAnswers).length;
    const totalQuestions = questions.length;
    const progressPercent = totalQuestions > 0 ? ((answeredCount / totalQuestions) * 100).toFixed(0) : 0;

    trackEvent('Life Theme Question Answer Public', {
      flow_type: 'public_question_flow',
      theme_id: themeId,
      theme_name: theme?.name || 'Unknown',
      question_number: currentQuestionIndex + 1,
      total_questions: totalQuestions,
      answer_value: value,
      progress_percent: progressPercent,
      answered_count: answeredCount
    });
  };

  const saveAnswersToStorage = (questionId, value) => {
    // Hämta befintliga svar
    const existing = sessionStorage.getItem('pendingLifeThemeAnswers');
    let allAnswers = existing ? JSON.parse(existing) : [];

    // Ta bort gammalt svar för samma fråga om det finns
    allAnswers = allAnswers.filter(a => a.question_id !== questionId);

    // Lägg till nytt svar
    allAnswers.push({
      theme_id: parseInt(themeId),
      question_id: questionId,
      answer_value: value,
      timestamp: new Date().toISOString()
    });

    sessionStorage.setItem('pendingLifeThemeAnswers', JSON.stringify(allAnswers));
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      
      // Track navigation
      trackEvent('Life Theme Question Previous Public', {
        flow_type: 'public_question_flow',
        theme_id: themeId,
        theme_name: theme?.name || 'Unknown',
        from_question: currentQuestionIndex + 1,
        to_question: currentQuestionIndex,
        total_questions: questions.length
      });
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      
      // Track navigation
      trackEvent('Life Theme Question Next Public', {
        flow_type: 'public_question_flow',
        theme_id: themeId,
        theme_name: theme?.name || 'Unknown',
        from_question: currentQuestionIndex + 1,
        to_question: currentQuestionIndex + 2,
        total_questions: questions.length
      });
    }
  };

  const handleComplete = () => {
    // Kontrollera att alla frågor är besvarade
    const unansweredCount = questions.filter(q => !answers[q.id]).length;
    
    if (unansweredCount > 0) {
      // Track incomplete attempt
      trackEvent('Life Theme Question Flow Incomplete Public', {
        flow_type: 'public_question_flow',
        theme_id: themeId,
        theme_name: theme?.name || 'Unknown',
        answered_count: Object.keys(answers).length,
        unanswered_count: unansweredCount,
        total_questions: questions.length
      });
      
      alert(`Du har ${unansweredCount} obesvarade frågor. Vänligen svara på alla frågor innan du fortsätter.`);
      // Hitta första obesvarade frågan
      const firstUnanswered = questions.findIndex(q => !answers[q.id]);
      if (firstUnanswered !== -1) {
        setCurrentQuestionIndex(firstUnanswered);
      }
      return;
    }

    // Track completion
    trackEvent('Life Theme Question Flow Complete Public', {
      flow_type: 'public_question_flow',
      theme_id: themeId,
      theme_name: theme?.name || 'Unknown',
      total_questions: questions.length,
      all_answered: true
    });

    // Alla frågor besvarade - gå till signup
    navigate('/express-signup');
  };

  const progressPercentage = questions.length > 0 
    ? ((Object.keys(answers).length / questions.length) * 100).toFixed(0)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface-primary to-surface-secondary flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-brand-primary animate-spin mx-auto mb-4" />
          <p className="text-secondary">Laddar frågor...</p>
        </div>
      </div>
    );
  }

  if (error || questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface-primary to-surface-secondary flex items-center justify-center p-4">
        <div className="bg-primary p-8 rounded-2xl shadow-custom-lg border border-light max-w-md text-center">
          <p className="text-error-700 mb-4">{error || 'Inga frågor hittades för detta tema.'}</p>
          <button
            onClick={() => navigate('/utforska-livstema')}
            className="px-6 py-3 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-custom"
          >
            Tillbaka till temavalet
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion?.id];
  const allAnswered = questions.every(q => answers[q.id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-primary to-surface-secondary font-sans">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/utforska-livstema')}
            className="flex items-center gap-2 text-secondary hover:text-brand-primary transition-custom mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Tillbaka till temavalet
          </button>

          <h1 className="text-3xl font-bold text-primary mb-2">
            {theme?.name || theme?.title || 'Livstema'}
          </h1>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-secondary">
                Fråga {currentQuestionIndex + 1} av {questions.length}
              </span>
              <span className="text-sm font-semibold text-brand-primary">
                {progressPercentage}% klart
              </span>
            </div>
            <div className="w-full bg-surface-secondary rounded-full h-2.5">
              <div
                className="bg-brand-primary h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-primary p-8 rounded-2xl shadow-custom-lg border border-light mb-6">
          <p className="text-2xl text-primary mb-8 leading-relaxed">
            {currentQuestion?.question_text}
          </p>

          {/* Answer Options */}
          <div className="space-y-3">
            {ANSWER_SCALE.map((option) => {
              const isSelected = currentAnswer === option.value;
              
              return (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(currentQuestion.id, option.value)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 flex items-center justify-between ${
                    isSelected
                      ? 'border-brand-primary bg-brand-primary/10 text-brand-primary'
                      : 'border-light hover:border-brand-primary/50 text-primary hover:bg-surface-secondary'
                  }`}
                >
                  <div>
                    <span className="font-semibold mr-2">{option.value}.</span>
                    <span>{option.label}</span>
                  </div>
                  {isSelected && (
                    <CheckCircle className="w-6 h-6 text-brand-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="flex items-center gap-2 px-6 py-3 bg-surface-primary border border-light rounded-lg hover:bg-surface-secondary transition-custom disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-5 h-5" />
            Föregående
          </button>

          {currentQuestionIndex === questions.length - 1 && allAnswered ? (
            <button
              onClick={handleComplete}
              className="flex items-center gap-2 px-8 py-3 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-custom font-semibold"
            >
              Slutför och skapa konto
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={currentQuestionIndex === questions.length - 1}
              className="flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-custom disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Nästa
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Answer Status */}
        <div className="mt-6 text-center">
          <p className="text-sm text-secondary">
            {Object.keys(answers).length} av {questions.length} frågor besvarade
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThemeQuestionFlow;
